// Greenfield consultants API client. Consumes the new endpoints written
// against the Entra-keyed schema (see docs/spec/target-schema.md).
//
// This module lives alongside the legacy src/lib/api/profile.ts during the
// migration. Delete profile.ts when every consumer has moved to this file.

import { acquireLensApiToken } from "../auth/msal";

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await acquireLensApiToken();
  const res = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${method} ${path} → ${res.status}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── Types (mirror the backend shapes) ────────────────────────────────────

export type Seniority = "junior" | "medior" | "senior" | "lead" | "principal";

/**
 * One entry in the consultants list. Merges Entra identity with any Lens-
 * side row that exists for that OID.
 */
export interface ConsultantListEntry {
  oid: string;
  displayName: string | null;
  mail: string | null;
  jobTitle: string | null;
  inDirectory: boolean;   // true if a current enabled Member user in the Entra tenant
  hasLensRow: boolean;    // true if a core.consultants row exists in Postgres
  active: boolean;
  nonConsulting: boolean;
  seniority: Seniority | null;
}

/**
 * Hydrated single-consultant response. Any of identity, lensRow, or
 * currentSections may be null / empty on a fresh consultant with no Lens
 * data yet.
 */
export interface ConsultantDetail {
  identity: {
    oid: string;
    displayName: string | null;
    givenName: string | null;
    surname: string | null;
    mail: string | null;
    userPrincipalName: string | null;
    jobTitle: string | null;
    officeLocation: string | null;
    mobilePhone: string | null;
  } | null;
  lensRow: {
    entraOid: string;
    entraDisplayName: string | null;
    phone: string | null;
    location: string | null;
    linkedinUrl: string | null;
    dateOfBirth: string | null;
    employer: string | null;
    seniority: Seniority | null;
    nonConsulting: boolean;
    active: boolean;
    masterCvText: string | null;
    stackDrawerId: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
  currentSections: Record<string, ProfileSectionRow>;
}

export type ProfileSectionKind =
  | "work_experience" | "education" | "certifications" | "languages"
  | "personal" | "availability" | "summary" | "skills" | "achievements"
  | "sector_experience";

export interface ProfileSectionRow {
  id: string;
  consultantOid: string;
  sectionKind: ProfileSectionKind;
  versionNumber: number;
  content: unknown;
  isCurrent: boolean;
  basedOnVersionId: string | null;
  aiOperationEventId: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: string;
  deletedAt: string | null;
}

export interface ConsultantWriteBody {
  seniority?: Seniority | null;
  nonConsulting?: boolean;
  active?: boolean;
  phone?: string | null;
  location?: string | null;
  linkedinUrl?: string | null;
  dateOfBirth?: string | null;   // ISO date
  employer?: string | null;
  stackDrawerId?: string | null;
}

// ── Endpoints ─────────────────────────────────────────────────────────────

/** GET /api/consultants — Entra tenant users ⋈ Postgres.
 *
 * Tolerates two response shapes while the greenfield backend rolls out:
 *   - v2 (target):   { consultants: [...] }
 *   - v1 (legacy):   [ { folder, name, email }, ... ]  → mapped to a stub
 *     that renders in the list but shows inDirectory=false + hasLensRow=true.
 */
export async function listConsultants(): Promise<ConsultantListEntry[]> {
  const raw = await req<unknown>("GET", "/api/consultants");
  if (raw && typeof raw === "object" && "consultants" in (raw as Record<string, unknown>)) {
    return (raw as { consultants: ConsultantListEntry[] }).consultants ?? [];
  }
  if (Array.isArray(raw)) {
    // Legacy shape adapter — will disappear once the new backend deploys.
    return (raw as Array<{ id?: string; folder?: string; name?: string; email?: string | null }>).map((r) => ({
      oid: r.id ?? r.folder ?? "",
      displayName: r.name ?? null,
      mail: r.email ?? null,
      jobTitle: null,
      inDirectory: false,
      hasLensRow: true,
      active: true,
      nonConsulting: false,
      seniority: null,
    }));
  }
  return [];
}

/** GET /api/consultants/:oid — hydrated single consultant. */
export async function getConsultant(oid: string): Promise<ConsultantDetail> {
  return req<ConsultantDetail>("GET", `/api/consultants/${encodeURIComponent(oid)}`);
}

/** POST /api/consultants/:oid — upsert Lens-side fields. */
export async function upsertConsultant(oid: string, body: ConsultantWriteBody): Promise<ConsultantDetail["lensRow"]> {
  return req("POST", `/api/consultants/${encodeURIComponent(oid)}`, body);
}

/** PATCH /api/consultants/:oid — update Lens-side fields (row must exist). */
export async function updateConsultant(oid: string, body: ConsultantWriteBody): Promise<ConsultantDetail["lensRow"]> {
  return req("PATCH", `/api/consultants/${encodeURIComponent(oid)}`, body);
}

/** POST /api/consultants/:oid/deactivate — admin soft-delete. */
export async function deactivateConsultant(oid: string): Promise<void> {
  return req("POST", `/api/consultants/${encodeURIComponent(oid)}/deactivate`);
}

/** POST /api/consultants/:oid/reactivate — admin restore. */
export async function reactivateConsultant(oid: string): Promise<void> {
  return req("POST", `/api/consultants/${encodeURIComponent(oid)}/reactivate`);
}
